import type React from "react";
import { useState } from "react";
import {
  Box,
  TextField,
  Typography,
  Button,
  Grid,
  CircularProgress,
  useTheme,
} from "@mui/material";
import { tokens } from "../../../theme";
import { Link, useNavigate } from "react-router-dom";
import { ArrowBackIos, CheckCircle } from "@mui/icons-material";
import { toast } from "react-toastify";
import type { ToastOptions } from "react-toastify";
import { motion } from "framer-motion";
import muzukuruLogo from "../../../assets/muzukurublack.png";
import ctaImage1 from "../../../assets/cta-pattern.png";
import ctaImage2 from "../../../assets/backgroundImage.jpg";
import { showSuccessToast } from "../../../components/common/ShowToast";
import apiService, { BaseURL } from "../../../service/Api";
import CtaSlideshow from "./CTASlideshow";

const ForgotPassword = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const [email, setEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isEmailSent, setIsEmailSent] = useState(false);
  const navigate = useNavigate();
  const ctaImages = [ctaImage1, ctaImage2];

  const showErrorToast = (message: string, toastId: string = "error-toast") => {
    toast.dismiss(toastId);
    const options: ToastOptions = {
      toastId,
      position: "top-right",
      autoClose: 5000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      theme: "light",
      style: {
        backgroundColor: "#fff",
        color: "#333",
        border: "1px solid #C29363",
        zIndex: 9999,
      },
    };
    toast.error(message, options);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) {
      showErrorToast("Please enter your email address.");
      return;
    }

    setIsLoading(true);
    try {
      await apiService.post(
        "/api/authentication/forgot-password/",
        { email },
        true,
      );
      setIsEmailSent(true);
      showSuccessToast("Password reset link sent to your email.");
      setTimeout(() => navigate("/login"), 5000);
    } catch (err: unknown) {
      let errorMessage = "An error occurred. Please try again.";
      const errorResponse = (
        err as {
          response?: { data?: { message?: string } };
          message?: string;
        }
      )?.response;
      const errorMessageFromResponse = errorResponse?.data?.message;
      if (errorMessageFromResponse) {
        errorMessage = errorMessageFromResponse;
      } else if (
        (err as { message?: string })?.message?.includes("Network Error")
      ) {
        errorMessage = "Network error. Please check your connection.";
      }
      showErrorToast(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  // ---- Animation variants ---- //
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { duration: 0.5, staggerChildren: 0.1 },
    },
  };
  const itemVariants = {
    hidden: { opacity: 0, y: 18 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.45 } },
  };
  const successVariants = {
    hidden: { opacity: 0, scale: 0.8 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: { duration: 0.5 },
    },
  };

  const fieldSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: "8px",
      backgroundColor: colors.primary[900],
      height: "52px",
      "& fieldset": { borderColor: "divider" },
      "&:hover fieldset": { borderColor: "text.secondary" },
      "&.Mui-focused fieldset": { borderColor: "#6b4c3b" },
    },
    "& .MuiInputBase-input": { color: "text.primary", fontSize: "15px" },
  };

  const isStaging =
    BaseURL?.includes("staging.muzukuru.com") || BaseURL?.includes("staging");

  return (
    <Box
      sx={{
        height: "100vh",
        width: "100%",
        overflow: "hidden",
        backgroundColor: colors.primary[900],
        position: "relative",
        border: isStaging ? "10px solid #FFBB00" : "none",
        boxSizing: "border-box",
      }}
    >
      <Grid container sx={{ height: "100%" }}>
        <Grid
          size={{ xs: 12, md: 6 }}
          sx={{
            height: "100%",
            backgroundColor: colors.primary[900],
            position: "relative",
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
            px: { xs: 3, sm: 5, md: 7 },
            py: { xs: 3, md: 4 },
          }}
        >
          {/* STAGING badge */}
          {isStaging && (
            <Box
              sx={{
                position: "absolute",
                left: -34,
                top: "50%",
                transform: "translateY(-50%) rotate(-90deg)",
                transformOrigin: "left center",
                backgroundColor: "#FFBB00",
                color: "#fff",
                fontWeight: 700,
                fontSize: "11px",
                letterSpacing: "2px",
                textTransform: "uppercase",
                px: 1.5,
                py: 0.6,
                borderRadius: "0 0 6px 6px",
                zIndex: 1000,
              }}
            >
              STAGING
            </Box>
          )}

          {/* Top bar */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: { xs: 3, md: 5 },
            }}
          >
            <Box
              component="img"
              src={muzukuruLogo}
              alt="Muzukuru Funeral Streaming"
              sx={{ height: 48, objectFit: "contain" }}
            />
          </Box>

          {/* Form content */}
          <Box
            sx={{
              flex: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {" "}
            <motion.div
              variants={containerVariants}
              initial="hidden"
              animate="visible"
              style={{ maxWidth: 420, width: "100%" }}
            >
              {!isEmailSent ? (
                <Box component="form" onSubmit={handleSubmit}>
                  {/* Back link */}
                  <motion.div variants={itemVariants}>
                    <Link
                      to="/login"
                      style={{
                        textDecoration: "none",
                        color: "#6b6b6b",
                        display: "flex",
                        alignItems: "center",
                        fontSize: "14px",
                        marginBottom: "16px",
                        fontWeight: 500,
                      }}
                    >
                      <ArrowBackIos />
                      {"Back to Sign In"}
                    </Link>
                  </motion.div>

                  <motion.div variants={itemVariants}>
                    <Typography
                      variant="h4"
                      sx={{
                        fontWeight: 700,
                        color: "text.primary",
                        mb: 0.5,
                        fontSize: { xs: "24px", md: "28px" },
                      }}
                    >
                      Forgot Password?
                    </Typography>
                  </motion.div>

                  <motion.div variants={itemVariants}>
                    <Typography
                      sx={{
                        color: "text.secondary",
                        fontSize: "15px",
                        mb: 4,
                        lineHeight: 1.6,
                      }}
                    >
                      {
                        "No worries! Enter your email address and we'll send you a link to reset your password."
                      }
                    </Typography>
                  </motion.div>

                  {/* Email */}
                  <motion.div variants={itemVariants}>
                    <Typography
                      sx={{
                        fontWeight: 600,
                        color: "text.primary",
                        fontSize: "14px",
                        mb: 1,
                      }}
                    >
                      Email Address
                    </Typography>
                    <TextField
                      placeholder="Enter your email address"
                      variant="outlined"
                      fullWidth
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      disabled={isLoading}
                      sx={{ ...fieldSx, mb: 3.5 }}
                    />
                  </motion.div>

                  {/* Send button */}
                  <motion.div variants={itemVariants}>
                    <Button
                      fullWidth
                      type="submit"
                      variant="contained"
                      disabled={isLoading}
                      sx={{
                        backgroundColor: "#6b4c3b",
                        textTransform: "none",
                        padding: "14px",
                        borderRadius: "8px",
                        fontSize: "16px",
                        fontWeight: 600,
                        boxShadow: "none",
                        color: "#fff",
                        mb: 3,
                        "&:hover": {
                          backgroundColor: "#55392d",
                          boxShadow: "none",
                        },
                        "&:disabled": {
                          backgroundColor: "rgba(107,76,59,0.4)",
                          color: "#fff",
                        },
                      }}
                    >
                      {isLoading ? (
                        <CircularProgress size={24} sx={{ color: "#fff" }} />
                      ) : (
                        "Send Reset Link"
                      )}
                    </Button>
                  </motion.div>

                  <motion.div variants={itemVariants}>
                    <Typography
                      align="center"
                      sx={{ color: "text.secondary", fontSize: "14px" }}
                    >
                      {"Remember your password? "}
                      <Link
                        to="/login"
                        style={{
                          color: "#6b4c3b",
                          fontWeight: 600,
                          textDecoration: "none",
                        }}
                      >
                        Sign in
                      </Link>
                    </Typography>
                  </motion.div>
                </Box>
              ) : (
                <motion.div
                  variants={successVariants}
                  initial="hidden"
                  animate="visible"
                  style={{ textAlign: "center" }}
                >
                  <Box sx={{ mb: 3 }}>
                    <CheckCircle
                      sx={{
                        fontSize: "72px",
                        color: "#6b4c3b",
                      }}
                    />
                  </Box>

                  <Typography
                    variant="h5"
                    sx={{ fontWeight: 700, color: "text.primary", mb: 2 }}
                  >
                    Check Your Email
                  </Typography>

                  <Typography
                    sx={{
                      color: "text.secondary",
                      mb: 4,
                      fontSize: "15px",
                      lineHeight: 1.6,
                    }}
                  >
                    {"We've sent a password reset link to "}
                    <strong>{email}</strong>
                    {
                      ". Please check your inbox and follow the instructions to reset your password."
                    }
                  </Typography>

                  <Box sx={{ mb: 3 }}>
                    <Typography sx={{ color: "text.secondary", mb: 2, fontSize: "14px" }}>
                      {"Didn't receive the email? Check your spam folder or"}
                    </Typography>
                    <Button
                      variant="outlined"
                      onClick={() => setIsEmailSent(false)}
                      sx={{
                        textTransform: "none",
                        borderColor: "rgba(107, 76, 59, 0.3)",
                        color: "#6b4c3b",
                        fontWeight: 600,
                        borderRadius: "8px",
                        "&:hover": {
                          borderColor: "#6b4c3b",
                          backgroundColor: "rgba(107, 76, 59, 0.04)",
                        },
                      }}
                    >
                      Try Again
                    </Button>
                  </Box>

                  <Link
                    to="/login"
                    style={{
                      color: "#6b4c3b",
                      fontWeight: 600,
                      textDecoration: "none",
                      fontSize: "15px",
                    }}
                  >
                    {"<- Back to Sign In"}
                  </Link>
                </motion.div>
              )}
            </motion.div>
          </Box>
        </Grid>

        <Grid
          size={{ xs: 0, md: 6 }}
          sx={{
            height: "100%",
            display: { xs: "none", md: "flex" },
            overflow: "hidden",
            backgroundColor: colors.primary[900],
            p: 2,
            boxSizing: "border-box",
          }}
        >
          <CtaSlideshow images={ctaImages} intervalMs={5000} />
        </Grid>
      </Grid>
    </Box>
  );
};

export default ForgotPassword;
